import { History, Lock, MessageSquare, Send } from "lucide-react";
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import { Textarea } from "../../../components/ui/textarea";
import { cn } from "../../../lib/utils";
import { useEncerrar, useMensagens, useResponder, useTicket } from "../hooks/use-suporte";
import { Message, STATUS_LABEL, isFinal } from "../types";
import { formatTelefone, horaMensagem } from "./format";
import { STATUS_BADGE } from "./status-colors";
import { HistoricoDialog } from "./historico-dialog";

const MAX_CHARS = 2000;

const remetente = (m: Message) => {
  if (m.sender_type === "contact") return "Entregador";
  if (m.sender_type === "bot") return "Assistente";
  if (m.sender_type === "agent") return m.pendente ? "Você" : m.sender_name ?? "Atendente";
  return null;
};

// Mensagens são texto puro e entram como filhos de texto do React (escapados),
// nunca como HTML.
function Bolha({ m }: { m: Message }) {
  if (m.sender_type === "system") {
    return <p className="text-center text-xs italic text-muted-foreground">{m.body}</p>;
  }
  const doAtendente = m.sender_type === "agent";
  return (
    <div className={cn("flex", doAtendente ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-lg px-3 py-2 text-sm",
          doAtendente ? "bg-primary text-primary-foreground" : "border bg-background",
          m.pendente && "opacity-70",
          m.sender_type === "bot" && "bg-muted"
        )}
      >
        <span className="mb-0.5 block text-xs font-semibold opacity-75">{remetente(m)}</span>
        <span className="whitespace-pre-wrap break-words">{m.body}</span>
        <span className="mt-1 block text-right text-[11px] opacity-60">
          {m.pendente ? "enviando…" : horaMensagem(m.created_at)}
        </span>
      </div>
    </div>
  );
}

export function TicketPanel({ ticketId }: { ticketId: string }) {
  const { data: ticket } = useTicket(ticketId);
  const closed = ticket ? isFinal(ticket.status) : false;
  const { data: conversa, isLoading } = useMensagens(ticketId, closed);
  const responder = useResponder(ticketId);
  const encerrar = useEncerrar();

  const [texto, setTexto] = useState("");
  const [confirmarEncerrar, setConfirmarEncerrar] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const fimRef = useRef<HTMLDivElement>(null);

  const mensagens = conversa?.items ?? [];
  const ultimaId = mensagens[mensagens.length - 1]?.msg_id;

  useEffect(() => {
    fimRef.current?.scrollIntoView({ block: "end" });
  }, [ultimaId, ticketId]);

  useEffect(() => setTexto(""), [ticketId]);

  // Limpa o campo na hora (a mensagem já aparece na conversa); se o envio
  // falhar e o campo ainda estiver vazio, devolve o texto para reenviar.
  const enviar = (e?: FormEvent) => {
    e?.preventDefault();
    const body = texto.trim();
    if (!body) return;
    setTexto("");
    responder.mutate(
      { body, clientMsgId: crypto.randomUUID() },
      { onError: () => setTexto((atual) => atual || body) }
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  if (!ticket) return <p className="p-6 text-sm text-muted-foreground">Carregando atendimento…</p>;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{formatTelefone(ticket.telefone)}</h2>
            <Badge variant="outline">{ticket.ops}</Badge>
            <Badge variant="outline" className={cn("border-transparent", STATUS_BADGE[ticket.status])}>
              {STATUS_LABEL[ticket.status]}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {ticket.assigned_agent ? `Atendente: ${ticket.assigned_agent}` : "Sem atendente"}
            {ticket.closed_by && ` · encerrado por ${ticket.closed_by}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistoricoAberto(true)}>
            <History /> Histórico
          </Button>
          {!closed && (
            <Button variant="destructive" size="sm" onClick={() => setConfirmarEncerrar(true)}>
              <Lock /> Encerrar
            </Button>
          )}
        </div>
      </div>

      {ticket.triage_summary && (
        <div className="border-b bg-muted/40 px-4 py-3">
          <p className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground">
            <MessageSquare className="h-3 w-3" /> Resumo da triagem
          </p>
          <p className="whitespace-pre-wrap text-sm">{ticket.triage_summary}</p>
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {isLoading && <p className="text-sm text-muted-foreground">Carregando mensagens…</p>}
        {mensagens.map((m) => (
          <Bolha key={m.msg_id} m={m} />
        ))}
        <div ref={fimRef} />
      </div>

      <form onSubmit={enviar} className="flex gap-2 border-t p-3">
        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value.slice(0, MAX_CHARS))}
          onKeyDown={onKeyDown}
          placeholder={closed ? "Atendimento encerrado" : "Responder ao entregador (Enter envia, Shift+Enter quebra linha)"}
          disabled={closed}
          rows={2}
          className="min-h-0 resize-none"
        />
        <Button type="submit" disabled={closed || !texto.trim()} className="self-end">
          <Send /> Enviar
        </Button>
      </form>

      <ConfirmDialog
        open={confirmarEncerrar}
        onOpenChange={setConfirmarEncerrar}
        title="Encerrar atendimento?"
        description="O entregador não poderá mais enviar mensagens neste atendimento. Um novo link abre um atendimento novo."
        confirmText="Encerrar"
        onConfirm={() => {
          encerrar.mutate(ticket.ticket_id);
          setConfirmarEncerrar(false);
        }}
      />

      <HistoricoDialog
        open={historicoAberto}
        onOpenChange={setHistoricoAberto}
        contactId={ticket.contact_id}
        titulo={`${formatTelefone(ticket.telefone)} · ${ticket.ops}`}
      />
    </div>
  );
}
